import { FC, FormEvent, ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultRoute } from '../../config/defaultRoute';
import { usePublicCmsSections } from '../../api/cmsHooks';
import { SectionRenderer } from '../../components/cms/SectionRenderer';
import { openCookieSettings } from '../../components/CookieConsent/cookieConsentStore';
import CairnStones from '../../components/CairnStones/CairnStones';
import { brandColors, colors, fontFamilies, radius } from '../../designTokens';

/**
 * Landing Page Component
 *
 * Public page of the People Rock programme (a JustASK Consulting programme), following the client's
 * layout: hero, context, statistics, model, journey (with the presentation video), value, technology,
 * audience, FAQ, about and contact. All copy lives in the `landing` namespace (PT/EN).
 *
 * **Important**: Authenticated users are redirected to their role-appropriate dashboard.
 */

const CONTACT_EMAIL = 'andreia.cruz@justask.pt';
const VIDEO_SRC = '/videos/people-rock.mp4';

// MUI multiplies a numeric `borderRadius` in `sx` by theme.shape.borderRadius (999 here), which turns
// every card into a pill. Use explicit px strings instead.
const R = { sm: `${radius.sm}px`, md: `${radius.md}px`, lg: `${radius.lg}px` };

// The public app bar is fixed (64px); anchored sections stop just below it.
const sectionSx = { py: { xs: 7, md: 10 }, scrollMarginTop: '64px' };

/** Reads an array translation (returnObjects) defensively. */
function useList<T>(key: string): T[] {
  const { t } = useTranslation('landing');
  const value = t(key, { returnObjects: true }) as unknown;
  return Array.isArray(value) ? (value as T[]) : [];
}

const Accent: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: fontFamilies.serif,
      fontStyle: 'italic',
      fontWeight: 500,
      color: colors.acentoTexto,
    }}
  >
    {children}
  </Box>
);

const SectionTitle: FC<{ plain: string; accent?: string; light?: boolean }> = ({
  plain,
  accent,
  light,
}) => (
  <Typography
    variant="h2"
    sx={{
      fontSize: { xs: '1.9rem', md: '2.4rem' },
      fontWeight: 600,
      lineHeight: 1.15,
      color: light ? brandColors.offWhite : 'text.primary',
      maxWidth: 760,
    }}
  >
    {plain}
    {accent && (
      <>
        {' '}
        <Accent>{accent}</Accent>
      </>
    )}
  </Typography>
);

/**
 * Presentation video, in the slot the client mockup reserves for the real platform capture (below the
 * journey steps): an off-white card like the steps around it, with the caption underneath.
 */
const LandingVideo: FC = () => {
  const { t } = useTranslation('landing');
  return (
    <Box
      component="figure"
      id="video"
      sx={{
        m: 0,
        p: { xs: 1.5, md: 2 },
        width: '100%',
        maxWidth: 880,
        // Centred with alignSelf: inside a Stack, `mx: auto` is reset by the spacing margins.
        alignSelf: 'center',
        mx: 'auto',
        backgroundColor: brandColors.offWhite,
        borderRadius: R.lg,
      }}
    >
      <Box
        component="video"
        controls
        preload="metadata"
        playsInline
        aria-label={t('journey.video_label')}
        data-testid="landing-video"
        sx={{
          display: 'block',
          width: '100%',
          aspectRatio: '16 / 9',
          objectFit: 'contain',
          borderRadius: R.md,
          backgroundColor: brandColors.petroleo,
        }}
      >
        <source src={VIDEO_SRC} type="video/mp4" />
      </Box>
      <Box component="figcaption" sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
        <Typography fontWeight={700}>{t('journey.video_title')}</Typography>
        <Typography variant="body1" color="text.secondary">
          {t('journey.video_caption')}
        </Typography>
      </Box>
    </Box>
  );
};

interface TitleText {
  title: string;
  text: string;
}

export const Landing: FC = () => {
  const { t, i18n } = useTranslation('landing');
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, activeRole } = useAuth();
  const [consent, setConsent] = useState(false);

  // CMS-driven content (ata #34) — falls back to the static design below until an admin has
  // authored and published sections in the active language for this page via /admin/cms.
  const { data: cmsSections } = usePublicCmsSections('landing', i18n.language.split('-')[0]);

  useEffect(() => {
    if (isAuthenticated) {
      if (!activeRole) {
        navigate('/select-role', { replace: true });
        return;
      }
      navigate(getDefaultRoute(activeRole), { replace: true });
    }
  }, [isAuthenticated, activeRole, navigate]);

  const contextChips = useList<string>('context.chips');
  const contextCards = useList<TitleText>('context.cards');
  const stats = useList<{ value: string; text: string }>('stats.items');
  const includes = useList<string>('model.includes');
  const steps = useList<TitleText>('journey.steps');
  const orgItems = useList<string>('value.organization.items');
  const personItems = useList<string>('value.person.items');
  const techCards = useList<TitleText>('tech.cards');
  const audienceChips = useList<string>('audience.chips');
  const faqItems = useList<{ q: string; a: string }>('faq.items');
  const aboutItems = useList<string>('about.items');

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!consent) return;
    const data = new FormData(event.currentTarget);
    const body = [
      `${t('contact.name')}: ${data.get('name') ?? ''}`,
      `${t('contact.organization')}: ${data.get('organization') ?? ''}`,
      `${t('contact.email')}: ${data.get('email') ?? ''}`,
      '',
      String(data.get('challenge') ?? ''),
    ].join('\n');
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      t('contact.mail_subject')
    )}&body=${encodeURIComponent(body)}`;
  };

  if (cmsSections && cmsSections.length > 0) {
    return (
      <>
        <SectionRenderer sections={cmsSections} />
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
          <LandingVideo />
        </Container>
      </>
    );
  }

  const plusSx = {
    alignSelf: 'center',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: brandColors.coral,
  } as const;

  const softCard = {
    backgroundColor: brandColors.nevoa,
    borderRadius: R.lg,
    p: 3,
    color: colors.tinta,
  };

  return (
    <Box>
      {/* Hero */}
      <Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gap: 6,
              gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' },
              alignItems: 'center',
            }}
          >
            <Stack spacing={3} alignItems="flex-start">
              <Typography
                variant="overline"
                sx={{ color: colors.acentoTexto, fontWeight: 700, letterSpacing: '0.08em' }}
              >
                {t('hero.kicker')}
              </Typography>
              <Typography
                variant="h1"
                sx={{ fontSize: { xs: '2.4rem', md: '3.4rem' }, fontWeight: 600, lineHeight: 1.08 }}
              >
                {t('hero.title_plain')} <Accent>{t('hero.title_accent')}</Accent>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 400, maxWidth: 560, lineHeight: 1.6 }}
              >
                {t('hero.subtitle')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" size="large" onClick={() => scrollTo('contacto')}>
                  {t('hero.cta_primary')}
                </Button>
                <Button variant="outlined" size="large" onClick={() => scrollTo('programa')}>
                  {t('hero.cta_secondary')}
                </Button>
              </Stack>
            </Stack>
            <Stack
              spacing={3}
              alignItems="center"
              data-testid="hero-visual"
              sx={{
                backgroundColor: brandColors.petroleo,
                borderRadius: R.lg,
                px: { xs: 3, md: 5 },
                py: { xs: 4, md: 6 },
              }}
            >
              <CairnStones
                tone="dark"
                base={t('hero.stone_base')}
                middle={t('hero.stone_middle')}
                top={t('hero.stone_top')}
              />
              <Typography
                sx={{
                  fontFamily: fontFamilies.serif,
                  fontStyle: 'italic',
                  fontSize: '1.25rem',
                  color: brandColors.coral,
                }}
              >
                {t('hero.tagline')}
              </Typography>
            </Stack>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              mt: 6,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            }}
          >
            {['pill1', 'pill2', 'pill3'].map((k) => (
              <Box key={k} sx={{ ...softCard, py: 1.5, textAlign: 'center', fontWeight: 500 }}>
                {t(`hero.${k}`)}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Context */}
      <Box id="casos" sx={sectionSx}>
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <SectionTitle plain={t('context.title')} />
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {contextChips.map((chip) => (
                <Chip key={chip} label={chip} variant="outlined" />
              ))}
            </Stack>
            <Typography
              sx={{
                fontFamily: fontFamilies.serif,
                fontStyle: 'italic',
                color: colors.acentoTexto,
                fontSize: '1.25rem',
              }}
            >
              {t('context.accent')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              }}
            >
              {contextCards.map((card) => (
                <Box key={card.title} sx={softCard}>
                  <Typography fontWeight={700}>{card.title}</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {card.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Statistics */}
      <Box
        sx={{ ...sectionSx, backgroundColor: brandColors.petroleo, color: brandColors.offWhite }}
      >
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <SectionTitle plain={t('stats.title')} light />
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              }}
            >
              {stats.map((stat) => (
                <Box
                  key={stat.value}
                  sx={{ border: `1px solid ${brandColors.menta}`, borderRadius: R.lg, p: 4 }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '2.6rem', md: '3.4rem' },
                      fontWeight: 600,
                      color: brandColors.menta,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography>{stat.text}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {t('stats.source')}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Model */}
      <Box id="programa" sx={sectionSx}>
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <SectionTitle plain={t('model.title_plain')} accent={t('model.title_accent')} />
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                alignItems: 'stretch',
                gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr auto 1fr' },
              }}
            >
              <Box sx={softCard}>
                <Typography fontWeight={700}>{t('model.platform.title')}</Typography>
                <Typography variant="body1">{t('model.platform.text')}</Typography>
              </Box>
              <Typography aria-hidden sx={plusSx}>
                +
              </Typography>
              <Box sx={softCard}>
                <Typography fontWeight={700}>{t('model.ai.title')}</Typography>
                <Typography variant="body1">{t('model.ai.text')}</Typography>
                <Chip
                  size="small"
                  label={t('model.ai.badge')}
                  sx={{ mt: 1, backgroundColor: brandColors.sol }}
                />
              </Box>
              <Typography aria-hidden sx={plusSx}>
                +
              </Typography>
              <Box sx={{ ...softCard, backgroundColor: brandColors.menta }}>
                <Typography fontWeight={700}>{t('model.human.title')}</Typography>
                <Typography variant="body1">{t('model.human.text')}</Typography>
              </Box>
            </Box>
            <Stack spacing={1.5}>
              <Typography variant="body1" fontWeight={700}>
                {t('model.includes_title')}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {includes.map((item) => (
                  <Chip key={item} label={item} variant="outlined" />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('model.note')}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Journey + video */}
      <Box id="percurso" sx={{ ...sectionSx, backgroundColor: brandColors.nevoa }}>
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <SectionTitle plain={t('journey.title_plain')} accent={t('journey.title_accent')} />
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                alignItems: 'start',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              }}
            >
              {steps.map((step, index) => (
                <Box
                  key={step.title}
                  data-testid="journey-step"
                  sx={{
                    backgroundColor: brandColors.offWhite,
                    borderRadius: R.lg,
                    p: 3,
                    // Each step sits a little higher than the one before: the path climbs, stone by stone.
                    mt: { md: (steps.length - 1 - index) * 3 },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                      fontWeight: 700,
                      backgroundColor: [
                        brandColors.coral,
                        brandColors.menta,
                        brandColors.petroleo,
                        brandColors.nevoa,
                      ][index % 4],
                      color: [colors.sobreAcento, colors.tinta, brandColors.offWhite, colors.tinta][index % 4],
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography fontWeight={700}>{step.title}</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {step.text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <LandingVideo />
          </Stack>
        </Container>
      </Box>

      {/* Value */}
      <Box id="beneficios" sx={sectionSx}>
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Typography variant="overline" sx={{ color: colors.acentoTexto, fontWeight: 700 }}>
                {t('value.kicker')}
              </Typography>
              <SectionTitle plain={t('value.title')} />
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              }}
            >
              {[
                { key: 'organization', items: orgItems, bg: brandColors.menta },
                { key: 'person', items: personItems, bg: brandColors.nevoa },
              ].map(({ key, items, bg }) => (
                <Box
                  key={key}
                  sx={{ backgroundColor: bg, borderRadius: R.lg, p: 4, color: colors.tinta }}
                >
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {t(`value.${key}.title`)}
                  </Typography>
                  <Stack spacing={1.5}>
                    {items.map((item) => (
                      <Stack direction="row" spacing={1.5} key={item}>
                        <CheckCircleIcon fontSize="small" sx={{ mt: 0.3 }} />
                        <Typography>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Technology */}
      <Box sx={{ ...sectionSx, backgroundColor: brandColors.nevoa }}>
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <SectionTitle plain={t('tech.title_plain')} accent={t('tech.title_accent')} />
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              }}
            >
              {techCards.map((card) => (
                <Box
                  key={card.title}
                  sx={{ backgroundColor: brandColors.offWhite, borderRadius: R.lg, p: 3 }}
                >
                  <Typography fontWeight={700}>{card.title}</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {card.text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Link
              component={RouterLink}
              to="/privacy-policy"
              underline="always"
              sx={{ color: colors.acentoTexto, alignSelf: 'flex-start' }}
            >
              {t('tech.link')}
            </Link>
          </Stack>
        </Container>
      </Box>

      {/* Audience */}
      <Box sx={sectionSx}>
        <Container maxWidth="lg">
          <Stack spacing={3}>
            <SectionTitle plain={t('audience.title')} />
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {audienceChips.map((chip, index) => (
                <Chip
                  key={chip}
                  label={chip}
                  sx={{
                    backgroundColor: index === 0 ? brandColors.menta : brandColors.nevoa,
                    color: colors.tinta,
                  }}
                />
              ))}
            </Stack>
            <Typography fontWeight={600}>{t('audience.note')}</Typography>
          </Stack>
        </Container>
      </Box>

      {/* FAQ */}
      <Box id="perguntas" sx={sectionSx}>
        <Container maxWidth="md">
          <Stack spacing={3}>
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: '1.9rem', md: '2.2rem' }, fontWeight: 600 }}
            >
              {t('faq.title')}
            </Typography>
            <Stack spacing={1.5}>
              {faqItems.map((item, index) => (
                <Accordion
                  key={item.q}
                  disableGutters
                  elevation={0}
                  defaultExpanded={index === 0}
                  sx={{
                    backgroundColor: brandColors.nevoa,
                    borderRadius: `${R.md} !important`,
                    '&::before': { display: 'none' },
                    // The "+" turns into a "×" while the answer is open.
                    '& .MuiAccordionSummary-expandIconWrapper': { color: brandColors.coral },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(45deg)',
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<AddIcon />}>
                    <Typography fontWeight={600}>{item.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">{item.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* About */}
      <Box sx={sectionSx}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gap: 5,
            }}
          >
            <Stack spacing={2}>
              <Chip
                label={t('about.kicker')}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  backgroundColor: brandColors.menta,
                  color: colors.tinta,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              />
              <SectionTitle plain={t('about.title')} />
              <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 1 }}>
                {aboutItems.map((item) => (
                  <Typography component="li" key={item} color="text.secondary">
                    {item}
                  </Typography>
                ))}
              </Box>
              <Link
                href="https://justask.pt"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
              >
                {t('about.justask_link')} →
              </Link>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Contact */}
      <Box id="contacto" sx={{ pb: { xs: 7, md: 10 }, scrollMarginTop: '80px' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: brandColors.petroleo,
              color: brandColors.offWhite,
              borderRadius: R.lg,
              p: { xs: 3, md: 6 },
              display: 'grid',
              gap: 4,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            <Stack spacing={2}>
              <Typography
                variant="h2"
                sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' }, fontWeight: 600 }}
              >
                {t('contact.title')}
              </Typography>
              <Typography sx={{ opacity: 0.9 }}>{t('contact.text')}</Typography>
            </Stack>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2 }}>
              {/* Labels sit above the fields, as in the client mockup. */}
              {(['name', 'organization', 'email', 'challenge'] as const).map((field) => {
                const required = field === 'name' || field === 'email';
                return (
                  <Stack key={field} spacing={0.75}>
                    <Typography component="label" htmlFor={`contact-${field}`} variant="body2">
                      {t(`contact.${field}`)}
                      {required && ' *'}
                      {field === 'challenge' && ` ${t('contact.optional')}`}
                    </Typography>
                    <TextField
                      id={`contact-${field}`}
                      name={field}
                      type={field === 'email' ? 'email' : 'text'}
                      required={required}
                      multiline={field === 'challenge'}
                      minRows={field === 'challenge' ? 3 : undefined}
                      hiddenLabel
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: brandColors.offWhite,
                          borderRadius: R.sm,
                        },
                      }}
                    />
                  </Stack>
                );
              })}
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {t('contact.no_health_note')}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    sx={{ color: brandColors.offWhite }}
                  />
                }
                label={
                  <Typography variant="caption">
                    {t('contact.consent_pre')}{' '}
                    <Link
                      component={RouterLink}
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      {t('contact.consent_link')}
                    </Link>
                    {t('contact.consent_post')}
                  </Typography>
                }
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!consent}
                sx={{
                  // MUI's default disabled grey disappears on the petroleum card.
                  '&.Mui-disabled': {
                    backgroundColor: brandColors.coral,
                    color: colors.sobreAcento,
                    opacity: 0.5,
                  },
                }}
              >
                {t('contact.submit')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, py: 4 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Typography
              sx={{ fontFamily: fontFamilies.serif, fontSize: '1.4rem', fontWeight: 600 }}
            >
              {t('footer.tagline_plain')} <Accent>{t('footer.tagline_accent')}</Accent>
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body1">
                <Link component={RouterLink} to="/privacy-policy">
                  {t('footer.privacy')}
                </Link>{' '}
                ·{' '}
                <Link component={RouterLink} to="/privacy-policy">
                  {t('footer.protect')}
                </Link>{' '}
                ·{' '}
                <Link component={RouterLink} to="/cookies">
                  {t('footer.cookies')}
                </Link>{' '}
                ·{' '}
                <Link component="button" type="button" onClick={openCookieSettings} sx={{ verticalAlign: 'baseline' }}>
                  {t('footer.cookie_settings')}
                </Link>{' '}
                · <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('footer.legal')}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
